import { apiPost } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';

export type SubscriptionCheckoutMode = 'pay' | 'request';

export type SubscriptionCheckoutAddressPayload = {
  address_id?: number | string;
  customer_address_id?: number | string;
};

export type BuildSubscriptionCheckoutInput = {
  plan: Record<string, unknown>;
  mode: SubscriptionCheckoutMode;
  customer_id: number;
  start_date_ymd: string;
  address_payload: SubscriptionCheckoutAddressPayload;
  day_off_array: number[];
  coupon_applied: boolean;
  coupon_code: string;
  celebrity_id?: number;
  selected_payment?: string;
  payment_type?: number;
  is_hesabe_gateway?: boolean;
  points_discount?: number;
  use_points?: boolean;
  referral_reward_claim_id?: number;
  selected_addons?: Array<{ id: number | string; selectedQty: number }>;
};

/** Custom builder tab sets `isCustomAllowed: true` explicitly — do not infer from menu `is_customized`. */
export function isCustomSubscriptionCheckoutPlan(plan: Record<string, unknown>): boolean {
  return plan.isCustomAllowed === true;
}

export function resolveCheckoutPlanIds(plan: Record<string, unknown>): {
  subscription_id: number;
  subscription_category_id: number;
} {
  /**
   * Standard checkout POST uses `plan.id` (subscription template id, e.g. 26-day Slim Fit = 36).
   * Category id (e.g. Slim Fit program = 35) is separate — never fall back to plan.id/subscription_id.
   */
  const subscription_id = Number(plan.id ?? plan.subscription_id ?? 0);
  const subscription_category_id = Number(plan.subscription_category_id ?? plan.category_id ?? 0);
  return { subscription_id, subscription_category_id };
}

export function isUsableCheckoutPlan(plan: unknown): plan is Record<string, unknown> {
  if (plan == null || typeof plan !== 'object') {
    return false;
  }
  const record = plan as Record<string, unknown>;
  const id = record.id ?? record.subscription_id;
  return id != null && String(id) !== '' && Number(id) > 0;
}

export function buildSubscriptionCheckoutRequest(input: BuildSubscriptionCheckoutInput): {
  endpoint: string;
  body: Record<string, unknown>;
} {
  const {
    plan,
    mode,
    customer_id,
    start_date_ymd,
    address_payload,
    day_off_array,
    coupon_applied,
    coupon_code,
    celebrity_id,
    selected_payment,
    payment_type,
    is_hesabe_gateway,
    points_discount,
    use_points,
    referral_reward_claim_id,
    selected_addons,
  } = input;

  const is_custom = isCustomSubscriptionCheckoutPlan(plan);
  const { subscription_id, subscription_category_id } = resolveCheckoutPlanIds(plan);

  // Never send Arabic-Indic / localized digits in checkout payloads — backend parsers break.
  const start_date_ascii = ensureAsciiDateString(start_date_ymd);

  const shared = {
    customer_id,
    start_date: start_date_ascii,
    coupon_code: coupon_applied ? coupon_code : '',
    ...address_payload,
    no_meals_on_days: day_off_array,
    celebrity_id: (celebrity_id ?? Number(plan.celebrity_id ?? 0)) || 0,
    referral_reward_claim_id: referral_reward_claim_id ?? 0,
  };

  if (is_custom) {
    const category_id = Number(plan.subscription_category_id ?? plan.subscription_id ?? subscription_category_id ?? 0);
    if (!category_id) {
      throw new Error('Missing subscription category for custom plan checkout.');
    }
    const body: Record<string, unknown> = {
      ...shared,
      subscription_category_id: category_id,
      days: plan.days,
      number_of_snacks: plan.number_of_snacks,
      number_of_meals: plan.number_of_meals,
      customized_meals: plan.customized_meals ?? [],
    };
    if (mode === 'request') {
      body.subscription_id = Number(plan.id ?? plan.subscription_id ?? category_id);
    }
    if (mode === 'pay') {
      body.payment_gateway = selected_payment ?? '';
      body.payment_type = is_hesabe_gateway ? payment_type : 1;
      body.redeem_from_loyalty = use_points ? points_discount : 0;
    }
    return {
      /** Legacy backup: both pay + request POST `customer_subscriptions/purchase/custom`. */
      endpoint: endpoints.subscriptionCustomValidate,
      body,
    };
  }

  if (!subscription_id) {
    throw new Error('Missing subscription plan id for checkout.');
  }

  const body: Record<string, unknown> = {
    ...shared,
    subscription_id,
    addons: (selected_addons ?? []).map(addon => ({ id: addon.id, quantity: addon.selectedQty })),
  };

  if (mode === 'pay') {
    body.payment_gateway = selected_payment ?? '';
    body.payment_type = is_hesabe_gateway ? payment_type : 1;
    body.redeem_from_loyalty = use_points ? points_discount : 0;
  }

  return {
    /** Legacy backup: both pay + request POST `customer_subscriptions` (payment fields omitted for request). */
    endpoint: endpoints.mySubscriptions,
    body,
  };
}

export async function postSubscriptionCheckoutRequest(request: {
  endpoint: string;
  body: Record<string, unknown>;
}): Promise<any> {
  return apiPost(request.endpoint, request.body);
}
